import { Link } from "react-router-dom";
import { Building2, Plus, Loader2, ArrowRight } from "lucide-react";
import { useGetMyFirmApplicationsQuery, useGetFirmEligibilityQuery } from "../api/firmApi";
import FirmStatusBadge from "../components/FirmStatusBadge";

export default function MyFirmApplicationsPage() {
  const { data: apps = [], isLoading } = useGetMyFirmApplicationsQuery();
  const { data: elig } = useGetFirmEligibilityQuery();

  const hasDraft = apps.some((a: any) => ["draft", "pending_payment", "returned"].includes(a.status));

  return (
    <div className="w-full p-4 md:p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Firm Applications</h1>
          <p className="text-sm text-gray-500">Register your quantity surveying practice firm.</p>
        </div>
        {(elig?.can_apply || hasDraft) && (
          <Link to="/firm-applications/new"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800">
            <Plus className="w-4 h-4" /> {hasDraft ? "Continue application" : "New application"}
          </Link>
        )}
      </div>

      {elig && !elig.can_apply && !hasDraft && (
        <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <p className="font-medium mb-1">You can't start a firm application right now:</p>
          <ul className="list-disc ml-5">{(elig.reasons ?? []).map((r: string, i: number) => <li key={i}>{r}</li>)}</ul>
        </div>
      )}

      {isLoading ? (
        <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : apps.length === 0 ? (
        <div className="border border-dashed rounded-xl py-16 text-center text-gray-400">
          <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No firm applications yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {apps.map((a: any) => (
            <Link key={a.id} to={`/firm-applications/${a.id}`}
              className="flex items-center gap-4 bg-white border rounded-xl p-4 hover:shadow-sm transition">
              <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center text-white shrink-0">
                <Building2 className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{a.practice_name ?? "Untitled firm"}</p>
                <p className="text-xs text-gray-400">
                  #{String(a.id).padStart(6, "0")} · {a.partners_count ?? 0} partner(s) · {a.referees_count ?? 0} referee(s)
                </p>
              </div>
              <FirmStatusBadge status={a.status} />
              <ArrowRight className="w-4 h-4 text-gray-300" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
